<template>
  <v-card class="mw-650 px-5 pb-5 pt-7 filters--report" outlined>
    <v-form ref="form filters">
      <v-select
        v-model="selectedReportType"
        :label="$t('report.filters.type')"
        :items="reportTypes"
        :disabled="isLoading"
        item-value="value"
        item-text="name"
        outlined
      />

      <v-spacer class="my-8" />

      <v-row>
        <v-col cols="12" :sm="dateFilterSize">
          <v-select
            v-model="sortDateBy"
            :label="$t('report.filters.date')"
            :items="dateSorts"
            :disabled="isLoading"
            item-value="value"
            item-text="name"
            outlined
          />
        </v-col>
        <v-col
          cols="12"
          sm="3"
          v-if="selectedReportType === 'APPOINTMENT_QUANTITY'"
        >
          <v-select
            v-model="groupBy"
            :label="$t('report.filters.grouping')"
            :items="groupingTypes"
            :disabled="isLoading"
            item-value="value"
            item-text="name"
            outlined
          />
        </v-col>
        <v-col cols="12" :sm="periodFilterSize">
          <v-menu :close-on-content-click="false" v-model="dateMenu" offset-y>
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="dates.text"
                @click:clear="clearDates"
                :label="$t('report.filters.period')"
                :disabled="isLoading"
                aria-disabled="true"
                v-on="on"
                clearable
                readonly
                outlined
              />
            </template>
            <v-date-picker
              v-model="selectedDates"
              :selected-items-text="
                $t('report.filters.datePicker.selectedItems')
              "
              :max="maxDateRange"
              :min="minDateRange"
              :locale="localeLanguage"
              color="primary"
              no-title
              range
              @input="handleDateChange"
            />
          </v-menu>
        </v-col>
      </v-row>

      <v-spacer class="my-8" />

      <HCitySelect v-model="selectedCityId" :country="country" />

      <v-spacer class="my-8" />

      <v-autocomplete
        v-if="!isServiceProviderUser"
        v-model="selectedBranchId"
        :label="$t('report.filters.branch')"
        :no-data-text="$t('report.filters.noData')"
        :items="branches"
        :disabled="isLoading"
        item-text="name"
        item-value="id"
        clearable
        outlined
      />

      <v-spacer class="my-8" />

      <v-autocomplete
        v-model="selectedAppointmentStatus"
        :label="$t('report.filters.status')"
        :no-data-text="$t('report.filters.noData')"
        :items="appointmentStatus"
        :disabled="isLoading"
        item-value="value"
        item-text="name"
        clearable
        outlined
      />

      <v-spacer class="my-8" />

      <v-row>
        <v-col cols="12" sm="6" align-self="center">
          <v-switch
            v-model="groupRealties"
            v-if="
              ['APPOINTMENT', 'APPOINTMENT_WITH_QUESTIONNAIRE'].includes(
                selectedReportType
              )
            "
            :label="$t('report.filters.groupRealties')"
            :disabled="isLoading"
            class="mt-0"
            hide-details
            inset
          />

          <v-divider class="d-sm-none mt-7" />
        </v-col>

        <v-col cols="6" sm="3">
          <v-btn
            @click="resetForm()"
            :disabled="isLoading"
            class="mr-5"
            large
            block
            text
          >
            {{ $t('report.buttons.reset') }}
          </v-btn>
        </v-col>

        <v-col cols="6" sm="3">
          <v-btn
            @click="generateReport()"
            :loading="isLoading"
            class="px-8"
            elevation="0"
            color="primary"
            large
            block
          >
            {{ $t('report.buttons.generate') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { catalogDateWithoutTime } from '@/util/catalog'
import { isDraftAllowedBrazilianCustomer } from '../../util/customer'
import HCitySelect from '../app/HCitySelect.vue'

const moment = require('moment')
export default {
  name: 'AppointmentReports',

  components: {
    HCitySelect,
  },

  data() {
    return {
      selectedReportType: 'APPOINTMENT',
      groupBy: 'MONTH',
      reportTypes: [
        { name: this.$t('report.types.appointment'), value: 'APPOINTMENT' },
        {
          name: this.$t('report.types.appointmentWithQuestionnaire'),
          value: 'APPOINTMENT_WITH_QUESTIONNAIRE',
        },
        {
          name: this.$t('report.types.appointmentQuantity'),
          value: 'APPOINTMENT_QUANTITY',
        },
      ],
      groupingTypes: [
        { name: this.$t('report.groupingTypes.month'), value: 'MONTH' },
        { name: this.$t('report.groupingTypes.week'), value: 'WEEK' },
        { name: this.$t('report.groupingTypes.day'), value: 'DAY' },
      ],
      dateSorts: [
        { name: this.$t('report.dateSorts.scheduled'), value: 'SCHEDULED' },
        { name: this.$t('report.dateSorts.creation'), value: 'CREATION' },
      ],
      defaultStatus: [
        { value: 'OPEN', name: this.$t('appointment.status.OPEN') },
        { value: 'SCHEDULED', name: this.$t('appointment.status.SCHEDULED') },
        { value: 'DONE', name: this.$t('appointment.status.DONE') },
        { value: 'CANCELED', name: this.$t('appointment.status.CANCELED') },
      ],

      internationalStatus: [
        { value: 'OPEN', name: this.$t('appointment.status.OPEN') },
        { value: 'SCHEDULED', name: this.$t('appointment.status.SCHEDULED') },
        { value: 'DONE', name: this.$t('appointment.status.DONE') },
        { value: 'CANCELED', name: this.$t('appointment.status.CANCELED') },
        { value: 'DRAFT', name: this.$t('appointment.status.DRAFT') },
        { value: 'REJECTED', name: this.$t('appointment.status.REJECTED') },
      ],
      groupRealties: true,
      sortDateBy: 'SCHEDULED',
      selectedDates: null,
      startDate: null,
      endDate: null,
      maxDateRange: null,
      minDateRange: null,
      dateMenu: false,
      selectedAppointmentStatus: null,
      selectedCityId: null,
      selectedBranchId: null,
      maxRange: 90,
    }
  },

  mounted() {
    if (!this.isServiceProviderUser) {
      this.fetchBranches()
    }
    if (this.country === 'BR' && isDraftAllowedBrazilianCustomer()) {
      this.defaultStatus.push({
        value: 'DRAFT',
        name: this.$t('appointment.status.DRAFT'),
      })
    }
  },

  computed: {
    ...mapGetters(['isLoading', 'cities', 'branches']),

    localeLanguage() {
      return localStorage.getItem('user_language') ?? 'pt-BR'
    },

    isServiceProviderUser() {
      var userType = localStorage.getItem('user_type')
      return userType === 'ROLE_SERVICE_PROVIDER'
    },

    dates() {
      if (this.selectedDates) {
        this.fixDateOrder(this.selectedDates[0], this.selectedDates[1])
        this.handleDateChange()
        let formattedDates = this.selectedDates.map((date) =>
          catalogDateWithoutTime(date)
        )
        return {
          text: formattedDates.join(' até '),
          value: this.selectedDates,
        }
      } else {
        return {
          text: this.$t('appointment.filters.selectDate'),
          value: null,
        }
      }
    },
    dateFilterSize() {
      return this.selectedReportType === 'APPOINTMENT_QUANTITY' ? '4' : '6'
    },
    periodFilterSize() {
      return this.selectedReportType === 'APPOINTMENT_QUANTITY' ? '5' : '6'
    },

    country() {
      return this.user?.country ?? localStorage.getItem('user_country')
    },

    appointmentStatus() {
      return this.country === 'BR'
        ? this.defaultStatus
        : this.internationalStatus
    },
  },

  methods: {
    ...mapActions([
      'generateAppointmentQuantityReport',
      'generateAppointmentWithQuestionnaireReport',
      'generateAppointmentReport',
      'fetchBranches',
    ]),

    clearDates() {
      this.resetDates()
    },

    handleDateChange() {
      const [startDateStr] = this.selectedDates

      this.$nextTick(() => {
        if (startDateStr) {
          this.minDateRange = moment(startDateStr).add(-1, 'days').toISOString()
          this.maxDateRange = moment(startDateStr).add(90, 'days').toISOString()
        }
      })
    },

    generateReport() {
      if (this.selectedReportType === 'APPOINTMENT') {
        this.generateAppointmentReport(this.preparedParams()).then(
          (response) => {
            if (response) {
              this.generateExcel(response)
            }
          }
        )
      } else if (this.selectedReportType === 'APPOINTMENT_QUANTITY') {
        this.generateAppointmentQuantityReport(this.preparedParams()).then(
          (response) => {
            if (response) {
              this.generateExcel(response)
            }
          }
        )
      } else if (this.selectedReportType === 'APPOINTMENT_WITH_QUESTIONNAIRE') {
        this.generateAppointmentWithQuestionnaireReport(
          this.preparedParams()
        ).then((response) => {
          if (response) {
            this.generateExcel(response)
          }
        })
      }
    },

    generateExcel(response) {
      let blob = new Blob([response], {
          type: 'application/vnd.ms-excel;charset=utf-8',
        }),
        url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'relatorio_de_agendamentos.xlsx')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    resetForm() {
      this.groupBy = 'MONTH'
      this.sortDateBy = 'SCHEDULED'
      this.selectedAppointmentStatus = null
      this.selectedCityId = null
      this.selectedBranchId = null
      this.resetDates()
    },

    resetDates() {
      this.startDate = null
      this.endDate = null
      this.selectedDates = null
      this.dates.value = null
      this.maxDateRange = null
      this.minDateRange = null
    },

    validateDates() {
      if (this.selectedDates && this.selectedDates.length > 0) {
        if (this.selectedDates[0] === this.selectedDates[1]) {
          this.defineDates(this.selectedDates[0], this.selectedDates[1])
        } else {
          this.fixDateOrder(this.selectedDates[0], this.selectedDates[1])
        }
      } else {
        this.defineDates(this.selectedDates[0], this.selectedDates[0])
      }
    },

    fixDateOrder(firstDate, secondDate) {
      if (this.selectedDates.length > 1) {
        try {
          if (moment(firstDate).isBefore(secondDate)) {
            this.defineDates(this.selectedDates[0], this.selectedDates[1])
          } else {
            this.defineDates(this.selectedDates[1], this.selectedDates[0])
          }

          this.selectedDates[0] = this.startDate
          this.selectedDates[1] = this.endDate
        } catch (error) {
          console.error(error)
        }
      } else {
        this.defineDates(this.selectedDates[0])
        this.selectedDates[0] = this.startDate
      }
    },

    defineDates(startDate, endDate) {
      this.startDate = moment(startDate)
        .startOf('day')
        .format('yyyy-MM-DDTHH:mm:ss')
      if (endDate) {
        this.endDate = moment(endDate)
          .endOf('day')
          .format('yyyy-MM-DDTHH:mm:ss')
      } else {
        this.startDate = moment(startDate)
          .endOf('day')
          .format('yyyy-MM-DDTHH:mm:ss')
      }
    },

    preparedParams() {
      let params = {}

      if (this.groupRealties) {
        params.group_realties = this.groupRealties
      }

      if (this.groupBy) {
        params.group_by = this.groupBy
      }

      if (this.sortDateBy) {
        params.sort_date_by = this.sortDateBy
      }

      if (this.selectedCityId) {
        params.city_id = this.selectedCityId
      }

      if (this.selectedBranchId) {
        params.branch_id = this.selectedBranchId
      }

      if (this.startDate) {
        params.start_date = this.startDate
      }

      if (this.endDate) {
        params.end_date = this.endDate
      }

      if (!this.endDate && !this.startDate) {
        params.start_date = moment()
          .add(-this.maxRange, 'days')
          .startOf('day')
          .format('yyyy-MM-DDTHH:mm:ss')
        params.end_date = moment().endOf('day').format('yyyy-MM-DDTHH:mm:ss')
      }

      if (this.selectedAppointmentStatus) {
        params.status = this.selectedAppointmentStatus
      }

      return params
    },
  },
}
</script>
